/**
 * A single submission lane in the Planning View timeline.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, CircleCheck, RotateCcw, RefreshCw, LoaderCircle } from 'lucide-react';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { usePermissions } from '@/lib/usePermissions';
import { useProject } from '@/lib/ProjectContext';
import { api } from '@/lib/api';
import type { Submission, SubmissionClassification } from '@/types/api';
import type { SubmissionMetrics } from '@/lib/submissionMetrics';
import { parseDateOnly, formatDateOnly } from '@/lib/dateOnly';
import { dateToPercent } from './TimelineGrid';
import { DatePopover } from './DatePopover';
import { CompletionDialog } from './CompletionDialog';

interface SubmissionLaneProps {
  submission: Submission;
  classification: SubmissionClassification | null;
  metrics: SubmissionMetrics | null;
  timelineStart: Date;
  timelineEnd: Date;
  onDatesChange: (submissionId: string, startDate: string, targetDate: string) => void;
  onCompletionChange: (submissionId: string) => void;
  onOverlayScan?: (submissionId: string) => Promise<void>;
  overlayScanningId?: string | null;
}

type Status = 'completed' | 'in-progress' | 'at-risk' | 'missing-dates';

function getStatus(
  sub: Submission,
  sectionsPct: number | null,
  contentDonePct: number | null,
  documentDonePct: number | null,
  consistencyPct: number | null,
): Status {
  // Persisted completion takes precedence over metric-derived status
  if (sub.completedAt) return 'completed';

  if (!sub.targetDate) return 'missing-dates';

  if (
    sectionsPct === 100 &&
    contentDonePct === 100 &&
    documentDonePct === 100 &&
    consistencyPct === 100
  )
    return 'completed';

  const target = parseDateOnly(sub.targetDate);
  if (!target) return 'missing-dates';
  const now = new Date();
  const daysUntil = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const anyBelow50 = [sectionsPct, contentDonePct, documentDonePct, consistencyPct].some(
    (p) => p !== null && p < 50,
  );

  if (daysUntil <= 21 && anyBelow50) return 'at-risk';

  return 'in-progress';
}

const STATUS_CONFIG: Record<Status, { label: string; bg: string; text: string; barColor: string }> = {
  'completed': { label: 'COMPLETED', bg: 'bg-emerald-100', text: 'text-emerald-700', barColor: 'bg-emerald-500' },
  'in-progress': { label: 'IN PROGRESS', bg: 'bg-amber-100', text: 'text-amber-700', barColor: 'bg-amber-400' },
  'at-risk': { label: 'AT RISK', bg: 'bg-red-100', text: 'text-red-700', barColor: 'bg-red-500' },
  'missing-dates': { label: 'MISSING DATES', bg: 'bg-slate-100', text: 'text-slate-500', barColor: 'bg-slate-300' },
};

function MetricValue({ label, pct, href, onClick }: { label: string; pct: number | null; href?: string; onClick?: () => void }) {
  const display = pct !== null ? `${pct}%` : '—%';
  const color = pct === null ? 'text-slate-400'
    : pct >= 80 ? 'text-emerald-600'
    : pct >= 50 ? 'text-amber-600'
    : 'text-red-600';

  const content = (
    <div className="text-center">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{display}</p>
    </div>
  );

  if (href) {
    return <Link to={href} onClick={onClick} className="hover:opacity-70 transition-opacity">{content}</Link>;
  }
  return content;
}

export function SubmissionLane({
  submission: sub,
  classification,
  metrics,
  timelineStart,
  timelineEnd,
  onDatesChange,
  onCompletionChange,
  onOverlayScan,
  overlayScanningId,
}: SubmissionLaneProps) {
  const navigate = useNavigate();
  const { setSubmissionFocusId } = useSubmissionFocus();
  const { activeProject } = useProject();
  const { canCompleteSubmission } = usePermissions();
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);

  // A2F-564: prefer backend-authoritative pct. Null when the submission type
  // has no recommended-section map (e.g. Response to Clinical Hold) — the
  // Gantt lane renders "—%" rather than a misleading 0%.
  const sectionsPct = classification?.sectionsPct ?? null;
  const contentDonePct = metrics?.contentDonePct ?? null;
  const documentDonePct = metrics?.documentDonePct ?? null;
  const consistencyPct = metrics?.consistencyPct ?? null;

  const status = getStatus(sub, sectionsPct, contentDonePct, documentDonePct, consistencyPct);
  const cfg = STATUS_CONFIG[status];

  const handleCompletion = async (comment: string) => {
    if (!activeProject) return;
    if (sub.completedAt) {
      // Reopen
      await api.projects.reopenSubmission(activeProject.id, sub.id, { comment });
    } else {
      // Complete — `signed_off_pct` is kept as the Content-Done snapshot key
      // for contract compat with readiness_snapshots (legacy name; same
      // semantics as the renamed UI field). `document_done_pct` is shipped
      // alongside so the audit row's `metrics_snapshot` carries Document
      // Done at completion time AND the backend's value-based override
      // detection (`metrics_snapshot.values()` in projects.py) sees
      // documentDonePct < 100 and tags the event as
      // `submission.completed.override`. Dropping it would split the
      // dialog's override warning from the audit classification.
      await api.projects.completeSubmission(activeProject.id, sub.id, {
        comment: comment || undefined,
        metrics_snapshot: {
          sections_pct: sectionsPct,
          signed_off_pct: contentDonePct,
          document_done_pct: documentDonePct,
          consistency_pct: consistencyPct,
        },
      });
    }
    setShowCompletionDialog(false);
    onCompletionChange(sub.id);
  };

  const handleCompletionWithConflict = async (comment: string) => {
    try {
      await handleCompletion(comment);
    } catch (err: unknown) {
      // Handle 409 conflict gracefully
      if (err instanceof Error && err.message.includes('409')) {
        onCompletionChange(sub.id);
        throw new Error(
          sub.completedAt
            ? 'This submission was already reopened by another user.'
            : 'This submission was already completed by another user.',
        );
      }
      throw err;
    }
  };

  // Timeline bar positioning
  const startDate = parseDateOnly(sub.startDate);
  const targetDate = parseDateOnly(sub.targetDate);
  const barLeft = startDate ? dateToPercent(startDate, timelineStart, timelineEnd) : null;
  const barRight = targetDate ? dateToPercent(targetDate, timelineStart, timelineEnd) : null;
  const barWidth = barLeft !== null && barRight !== null ? Math.max(barRight - barLeft, 1) : 0;

  const targetFmt = sub.targetDate
    ? formatDateOnly(sub.targetDate, { month: 'short', day: 'numeric' }).toUpperCase()
    : null;

  return (
    <div className="py-4 px-4 hover:bg-slate-50/50 transition-colors">
      {/* Info row */}
      <div className="flex items-center gap-4 mb-2">
        {/* Badge + Type — clickable, navigates to Dashboard */}
        <div
          className="flex items-center gap-2 min-w-[180px] cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => { setSubmissionFocusId(sub.id); navigate('/'); }}
        >
          <span className="text-[10px] font-bold text-white bg-brand-orange px-2 py-0.5 rounded-full whitespace-nowrap">
            SUB {sub.displaySequence}
          </span>
          {sub.submissionType && (
            <span className="text-sm font-bold text-brand-charcoal truncate">
              {sub.submissionType}
            </span>
          )}
        </div>

        {/* Inline metrics — set global focus before navigating. Both Done
            metrics deep-link to Signoff (the surface that owns
            both sign-off layers post-A2F-666 / A2F-998). */}
        <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
          <MetricValue label="% Sections Present" pct={sectionsPct} href="/plan/sections" onClick={() => setSubmissionFocusId(sub.id)} />
          <MetricValue label="% Content Done" pct={contentDonePct} href="/documents/signoff" onClick={() => setSubmissionFocusId(sub.id)} />
          <MetricValue label="% Document Done" pct={documentDonePct} href="/documents/signoff" onClick={() => setSubmissionFocusId(sub.id)} />
          <MetricValue label="Consistency" pct={consistencyPct} href="/" onClick={() => setSubmissionFocusId(sub.id)} />
          {consistencyPct === null && onOverlayScan && (
            overlayScanningId === sub.id ? (
              <LoaderCircle className="w-3.5 h-3.5 text-brand-orange animate-spin" />
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onOverlayScan(sub.id); }}
                className="flex items-center gap-1 text-[10px] font-semibold text-brand-orange hover:text-orange-600 transition-colors whitespace-nowrap"
                title="Scan this submission's files against the current IND"
              >
                <RefreshCw className="w-3 h-3" />
                SCAN
              </button>
            )
          )}
        </div>

        {/* Status badge */}
        <span className={`text-[10px] font-bold px-2 py-1 rounded ${cfg.bg} ${cfg.text} whitespace-nowrap`}>
          {cfg.label}
        </span>

        {/* Complete / Reopen button */}
        {canCompleteSubmission && (
          sub.completedAt ? (
            <button
              onClick={(e) => { e.stopPropagation(); setShowCompletionDialog(true); }}
              className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-brand-orange transition-colors whitespace-nowrap"
              title="Reopen this submission"
            >
              <RotateCcw className="w-3 h-3" />
              REOPEN
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setShowCompletionDialog(true); }}
              className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap"
              title="Mark this submission as complete"
            >
              <CircleCheck className="w-3 h-3" />
              MARK COMPLETE
            </button>
          )
        )}

        {/* Date entry / edit */}
        <div className="ml-auto text-right shrink-0 relative">
          {targetFmt ? (
            <button
              onClick={(e) => { e.stopPropagation(); setShowDatePopover(true); }}
              className="text-xs font-semibold text-slate-500 hover:text-brand-orange transition-colors"
            >
              TARGET: {targetFmt}
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setShowDatePopover(true); }}
              className="flex items-center gap-1 text-xs font-semibold text-brand-orange hover:text-brand-orange/80 transition-colors"
            >
              <Calendar className="w-3 h-3" />
              ADD DATES
            </button>
          )}
          {showDatePopover && (
            <DatePopover
              startDate={sub.startDate}
              targetDate={sub.targetDate}
              onSave={(s, t) => {
                setShowDatePopover(false);
                onDatesChange(sub.id, s, t);
              }}
              onCancel={() => setShowDatePopover(false)}
            />
          )}
        </div>
      </div>

      {/* Timeline bar */}
      {barWidth > 0 && (
        <div className="relative h-4 mt-1">
          <div
            className={`absolute top-0 h-full rounded-full ${cfg.barColor}`}
            style={{
              left: `${barLeft}%`,
              width: `${barWidth}%`,
              minWidth: '8px',
            }}
          />
        </div>
      )}

      {/* Missing start date prompt */}
      {!startDate && targetDate && (
        <div className="mt-1">
          <button
            onClick={() => setShowDatePopover(true)}
            className="text-[10px] text-slate-400 hover:text-brand-orange transition-colors"
          >
            + Add start date
          </button>
        </div>
      )}

      {/* Completion dialog */}
      {showCompletionDialog && (
        <CompletionDialog
          submissionLabel={`Sub ${sub.displaySequence}${sub.submissionType ? ' — ' + sub.submissionType : ''}`}
          sectionsPct={sectionsPct}
          contentDonePct={contentDonePct}
          documentDonePct={documentDonePct}
          consistencyPct={consistencyPct}
          isReopening={!!sub.completedAt}
          onConfirm={handleCompletionWithConflict}
          onClose={() => setShowCompletionDialog(false)}
        />
      )}
    </div>
  );
}
